import React, { useState } from "react";
import {
  ArrowLeft,
  UserPlus,
  Utensils,
  Users,
  Flame,
  Clock,
  Mail,
  CheckCircle2,
  ShieldCheck,
  Trophy,
  Medal,
  Award,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card } from "../../components/ui/card";
import welcomeBg from "../../assets/homebg.png";
import MealApprovalModal from "../../components/MealApprovalModal";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService from "../../services/HomeService";
import { pickGroupStreakFromHome } from "../../utils/normalizeGroupStreak";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getGlucoseStyle = (band?: string) => {
  switch (band?.toLowerCase()) {
    case "low":    return { bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500", label: "Low" };
    case "medium": return { bg: "bg-yellow-100 dark:bg-yellow-900/40",  text: "text-yellow-700 dark:text-yellow-300",  dot: "bg-yellow-500",  label: "Medium" };
    case "high":   return { bg: "bg-red-100 dark:bg-red-900/40",        text: "text-red-700 dark:text-red-300",        dot: "bg-red-500",    label: "High" };
    default:       return { bg: "bg-muted/60",                          text: "text-muted-foreground",                 dot: "bg-muted-foreground/40", label: "—" };
  }
};

const getBBSColor = (score?: number) => {
  if (!score) return "text-muted-foreground";
  if (score >= 75) return "text-emerald-600 dark:text-emerald-400";
  if (score >= 50) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
};

const getInitialColor = (index: number) => {
  const colors = [
    "bg-emerald-400",
    "bg-red-400",
    "bg-yellow-400",
    "bg-blue-400",
    "bg-purple-400",
    "bg-orange-400",
    "bg-pink-400",
    "bg-teal-400",
  ];
  return colors[index % colors.length];
};

const normalizeId = (id?: string) =>
  (id || "").toString().trim().replace(/-/g, "").toLowerCase();

// ─── Page ────────────────────────────────────────────────────────────────────

const GroupMembersListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Meal approval modal state
  const [mealApprovalToken, setMealApprovalToken] = useState<string | null>(null);
  const [showMealApprovalModal, setShowMealApprovalModal] = useState(false);
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const rawGroupId = searchParams.get("group_id") || localStorage.getItem("family_group_id") || undefined;
  const groupId = rawGroupId && uuidRegex.test(rawGroupId) ? rawGroupId : undefined;
  const groupName = searchParams.get("group_name") || "Group Members";

  const today = new Date().toISOString().split("T")[0];

  // Fetch Family Health for this group when `group_id` is known so the API returns the right slice.
  const { data: homeData, isLoading } = useQuery({
    queryKey: ["homePageData", today, groupId ?? "all"],
    queryFn: () => homeService.getHomePageData(today, groupId),
    staleTime: 30 * 1000,
    refetchOnMount: "always" as const,
  });

  const familyHealth = homeData?.["Family Health"];
  const groups: any[] = familyHealth?.groups || [];
  const requestedGroupName = (searchParams.get("group_name") || "").trim().toLowerCase();
  const normalizedGroupId = normalizeId(groupId);

  // Find the correct group robustly across API shape variants.
  const selectedGroup =
    groups.find((g: any) => normalizeId(g?.id) === normalizedGroupId) ||
    groups.find((g: any) => normalizeId(g?.family_group_id) === normalizedGroupId) ||
    groups.find((g: any) => normalizeId(g?.group_id) === normalizedGroupId) ||
    (requestedGroupName
      ? groups.find((g: any) => (g?.name || "").toString().trim().toLowerCase() === requestedGroupName)
      : undefined) ||
    groups.find((g: any) => g?.is_selected) ||
    groups[0];
  
  const rawMembers: any[] = selectedGroup?.members || [];
  // Sort by BBS score descending — members with no score go to the bottom
  const members: any[] = [...rawMembers].sort((a, b) => {
    const scoreA = a.bbs_score ?? -Infinity;
    const scoreB = b.bbs_score ?? -Infinity;
    return scoreB - scoreA;
  });
  const pendingInvites: any[] = selectedGroup?.pending_invites || [];
  const memberTotal = selectedGroup?.summary?.members_total ?? members.length;
  const pendingCount = selectedGroup?.summary?.pending_invites_count ?? pendingInvites.length;
  const activeTodayCount = selectedGroup?.summary?.active_today_count ?? members.filter((m: any) => (m.meals_logged || 0) > 0).length;
  const streak = pickGroupStreakFromHome(homeData, selectedGroup?.group_streak);
  const groupStreak = streak?.days ?? 0;
  const streakMood =
    streak?.label?.trim() ||
    (groupStreak > 0 ? "On fire!" : "Start together");
  const streakSub =
    groupStreak > 0
      ? "Keep the chain going — log at least one meal each day."
      : "Log meals as a group to begin your streak.";

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 overflow-y-auto"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="space-y-4 px-4 pb-20 pt-4">
          {/* Header */}
          <div className="flex items-center justify-between pb-2">
            <button
              onClick={() => navigate(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-card shadow-sm active:scale-95 transition-all"
            >
              <ArrowLeft className="h-5 w-5 text-foreground" strokeWidth={2.5} />
            </button>
            <button
              onClick={() =>
                navigate(`/glucera${GluceraNavigationRoutes.INVITE_MEMBER}${groupId ? `?group_id=${groupId}` : ""}`)
              }
              className="flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 px-3.5 py-2 text-caption1 font-semibold text-white shadow-md active:scale-95 transition-all"
            >
              <UserPlus className="h-4 w-4" strokeWidth={2.5} />
              Add Member
            </button>
          </div>

          {/* Group Info Card — informational, not interactive */}
          <section
            aria-label="Group summary"
            className="cursor-default select-none rounded-xl border border-violet-200/60 bg-gradient-to-br from-violet-50 to-purple-50/60 p-3.5 dark:border-violet-800/30 dark:from-violet-950/40 dark:to-purple-950/20"
          >
            <div className="flex items-start gap-2.5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-purple-600 shadow-sm">
                <Users className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-subhead font-bold text-foreground">{selectedGroup?.name || groupName}</p>
                <p className="text-caption1 text-muted-foreground mt-0.5">
                  {memberTotal} {memberTotal === 1 ? "member" : "members"}
                  {pendingCount > 0 && (
                    <span className="ml-1 text-amber-500">· {pendingCount} pending</span>
                  )}
                </p>
              </div>
            </div>
          </section>

          {/* Group quick summary — stat tiles (readable contrast; not interactive) */}
          <section aria-label="Member counts" className="cursor-default select-none space-y-2">
            <p className="px-0.5 text-compact-xs font-semibold uppercase tracking-wide text-foreground/70">
              Today&apos;s snapshot
            </p>
            <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl border-2 border-border bg-card px-3 py-2.5 text-center shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
              <p className="text-compact-xs font-semibold uppercase tracking-wide text-muted-foreground">Members</p>
              <p className="mt-0.5 text-callout font-black text-foreground">{memberTotal}</p>
            </div>
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-3 py-2.5 text-center shadow-sm dark:border-amber-600 dark:bg-amber-950/55">
              <p className="text-compact-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">Pending</p>
              <p className="mt-0.5 text-callout font-black text-amber-900 dark:text-amber-200">{pendingCount}</p>
            </div>
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5 text-center shadow-sm dark:border-emerald-600 dark:bg-emerald-950/55">
              <p className="text-compact-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">Active</p>
              <p className="mt-0.5 text-callout font-black text-emerald-900 dark:text-emerald-200">{activeTodayCount}</p>
            </div>
            </div>
          </section>

          {/* Group streak — full-width hero card */}
          <Card className="animate-in fade-in slide-in-from-bottom-3 relative overflow-hidden rounded-2xl border border-orange-200/50 bg-card shadow-lg shadow-orange-500/10 ring-1 ring-orange-100/80 duration-500 dark:border-orange-800/35 dark:bg-card dark:shadow-orange-900/20 dark:ring-orange-900/30">
            <div
              className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 animate-streak-blob rounded-full bg-gradient-to-br from-amber-400/30 to-orange-500/20 blur-2xl [animation-delay:0s] dark:from-amber-500/15 dark:to-orange-600/10"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-8 -left-8 h-32 w-32 animate-streak-blob rounded-full bg-gradient-to-tr from-rose-400/20 to-amber-300/25 blur-2xl [animation-delay:2.75s] dark:from-rose-600/10 dark:to-amber-600/10"
              aria-hidden
            />
            <div className="relative space-y-4 p-4 sm:p-5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="motion-safe:animate-streak-flame flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 shadow-md shadow-orange-500/30">
                  <Flame className="h-6 w-6 text-white" strokeWidth={2.25} />
                </div>
                <div className="min-w-0">
                  <p className="text-compact-sm font-bold uppercase tracking-[0.12em] text-orange-700/90 dark:text-orange-400">
                    Group streak
                  </p>
                  <p className="mt-0.5 text-caption1 leading-snug text-muted-foreground">
                    {streakSub}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-x-3 gap-y-1 border-b border-orange-200/40 pb-4 dark:border-orange-800/30">
                <span className="text-display-xs font-black leading-none tracking-tight text-transparent tabular-nums sm:text-6xl bg-gradient-to-br from-amber-600 via-orange-600 to-rose-600 bg-clip-text dark:from-amber-400 dark:via-orange-400 dark:to-rose-400">
                  {groupStreak}
                </span>
                <div className="pb-1">
                  <p className="text-callout font-bold text-foreground">
                    {groupStreak === 1 ? "day" : "days"}
                  </p>
                  <p className="text-compact-sm font-medium text-muted-foreground">in a row</p>
                </div>
                <span
                  className="ml-auto inline-flex max-w-full basis-full items-center rounded-lg border border-dashed border-amber-300/70 bg-amber-50/80 px-2.5 py-1.5 text-compact-sm font-semibold text-amber-950 dark:border-amber-600/40 dark:bg-amber-950/25 dark:text-amber-100 sm:basis-auto sm:text-caption1"
                  role="status"
                >
                  <span className="truncate">{streakMood}</span>
                </span>
              </div>

              {streak?.all_members_logged_today === true && (
                <div className="animate-in fade-in zoom-in-95 flex items-start gap-2.5 rounded-xl border border-emerald-200/90 bg-emerald-50/95 px-3 py-2.5 text-caption1 font-semibold leading-snug text-emerald-900 duration-300 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-100">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span>
                    Everyone logged at least one meal today. Nice work — come back tomorrow to grow the streak.
                  </span>
                </div>
              )}

              {streak?.requirement?.trim() ? (
                <div className="rounded-xl bg-muted/50 px-3 py-3 dark:bg-muted/25">
                  <p className="text-compact-xs font-bold uppercase tracking-wide text-muted-foreground sm:text-compact-sm">
                    How it works
                  </p>
                  <p className="mt-1.5 text-caption1 leading-relaxed text-foreground/90 sm:text-caption1">
                    {streak.requirement}
                  </p>
                </div>
              ) : null}
            </div>
          </Card>

          {/* Members list */}
          <div className="overflow-hidden rounded-2xl shadow-xl border border-violet-100 dark:border-violet-900/40">
            {/* Header */}
            <div className="relative overflow-hidden bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-4 py-4">
              <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-white/10 blur-xl" />
              <div className="pointer-events-none absolute -bottom-4 left-8 h-14 w-14 rounded-full bg-white/10 blur-xl" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                    <Trophy className="h-4 w-4 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-subhead font-black tracking-widest text-white uppercase">BBS Ranking</p>
                    <p className="text-compact-xs text-white/60 font-medium">Bite Balance Score</p>
                  </div>
                </div>
                <span className="tabular-nums text-compact-sm font-semibold text-white/90">
                  {members.length} members
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="divide-y divide-border/30 bg-card">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-10 sm:py-12 gap-2 sm:gap-3">
              <div className="h-7 w-7 sm:h-8 sm:w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
              <p className="text-caption1 sm:text-subhead text-muted-foreground">Loading members...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 px-4 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 shadow-lg">
                <UserPlus className="h-8 w-8 text-white" strokeWidth={2.5} />
              </div>
              <div className="space-y-1.5">
                <p className="text-callout font-bold text-foreground">No Members Yet</p>
                <p className="text-subhead text-muted-foreground max-w-[260px] mx-auto leading-relaxed">Start building your group! Invite family or friends to track health together.</p>
              </div>
              <button
                onClick={() =>
                  navigate(`/glucera${GluceraNavigationRoutes.INVITE_MEMBER}${groupId ? `?group_id=${groupId}` : ""}`)
                }
                className="mt-2 h-11 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-6 text-subhead font-semibold text-white shadow-md active:scale-95 transition-all"
              >
                Invite Member
              </button>
            </div>
          ) : (
            members.map((member: any, idx: number) => {
              const glucose = getGlucoseStyle(member.glucose_band);
              const hasBBS = !!member.bbs_score;
              const hasLogged = (member.meals_logged || 0) > 0;
              const approvalTokens: string[] = member.approval_tokens || [];
              const hasPendingApprovals = approvalTokens.length > 0;
              const rank = idx + 1;

              const rowAccent =
                rank === 1
                  ? "border-l-4 border-l-amber-400 bg-gradient-to-r from-amber-50/80 to-transparent dark:from-amber-950/30 dark:to-transparent"
                  : rank === 2
                  ? "border-l-4 border-l-slate-400 bg-gradient-to-r from-slate-50/80 to-transparent dark:from-slate-900/20 dark:to-transparent"
                  : rank === 3
                  ? "border-l-4 border-l-orange-400 bg-gradient-to-r from-orange-50/70 to-transparent dark:from-orange-950/20 dark:to-transparent"
                  : "border-l-4 border-l-transparent";

              return (
                <div
                  key={member.user_id || idx}
                  className={`flex w-full items-center gap-3 px-3 py-3 ${rowAccent}`}
                >
                  {/* Rank badge */}
                  <div className="shrink-0 flex flex-col items-center gap-0.5 w-9">
                    {rank === 1 ? (
                      <>
                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 shadow-lg shadow-amber-300/50 dark:shadow-amber-700/30">
                          <Trophy className="h-4.5 w-4.5 text-white" strokeWidth={2.5} />
                        </div>
                        <span className="text-2xs font-black text-amber-500 leading-none tracking-wide">#1</span>
                      </>
                    ) : rank === 2 ? (
                      <>
                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-400 to-slate-500 shadow-md shadow-slate-300/40 dark:shadow-slate-700/30">
                          <Medal className="h-4 w-4 text-white" strokeWidth={2.5} />
                        </div>
                        <span className="text-2xs font-black text-slate-500 leading-none tracking-wide">#2</span>
                      </>
                    ) : rank === 3 ? (
                      <>
                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 to-amber-600 shadow-md shadow-orange-300/40 dark:shadow-orange-700/30">
                          <Award className="h-4 w-4 text-white" strokeWidth={2.5} />
                        </div>
                        <span className="text-2xs font-black text-orange-500 leading-none tracking-wide">#3</span>
                      </>
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-border/50 bg-muted/30">
                        <span className="text-compact-sm font-bold text-muted-foreground">#{rank}</span>
                      </div>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full text-white font-bold text-subhead ring-2 ${
                        rank === 1 ? "ring-amber-400/60" : rank === 2 ? "ring-slate-400/60" : rank === 3 ? "ring-orange-400/60" : "ring-transparent"
                      } ${getInitialColor(idx)}`}
                    >
                      {member.initial || member.display_name?.[0] || "?"}
                    </div>
                    {member.is_current_user && (
                      <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-violet-600 px-1 py-0.5 text-4xs font-bold text-white leading-none">
                        YOU
                      </span>
                    )}
                  </div>

                  {/* Name + meals */}
                  <div className="min-w-0 flex-1">
                    <p className="text-subhead font-bold text-foreground truncate">
                      {member.is_current_user ? "You" : member.display_name}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Utensils className="h-3 w-3 text-muted-foreground/50" strokeWidth={2} />
                      <p className="text-compact-sm text-muted-foreground truncate">
                        {hasLogged
                          ? `${member.meals_logged} ${member.meals_logged === 1 ? "meal" : "meals"} logged`
                          : "Not logged yet"}
                      </p>
                    </div>
                  </div>

                  {/* BBS Score — read-only stat */}
                  <div className="shrink-0 text-center" role="presentation">
                    {hasBBS ? (
                      <div className={`rounded-lg border border-dashed px-2 py-1.5 ${
                        rank === 1 ? "border-amber-300/60 bg-amber-50/50 dark:border-amber-700/40 dark:bg-amber-950/25"
                        : rank === 2 ? "border-slate-300/60 bg-slate-50/50 dark:border-slate-600/40 dark:bg-slate-900/25"
                        : rank === 3 ? "border-orange-300/60 bg-orange-50/50 dark:border-orange-700/40 dark:bg-orange-950/25"
                        : "border-border/60 bg-muted/20"
                      }`}>
                        <p className={`text-callout font-black leading-none tabular-nums ${getBBSColor(member.bbs_score)}`}>
                          {Math.round(member.bbs_score)}
                        </p>
                        <p className="text-3xs font-semibold text-muted-foreground mt-0.5 uppercase tracking-wider">BBS</p>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border/50 bg-muted/10 px-2 py-1.5">
                        <p className="text-callout font-black text-muted-foreground/30 leading-none">—</p>
                        <p className="text-3xs font-semibold text-muted-foreground/40 mt-0.5 uppercase tracking-wider">BBS</p>
                      </div>
                    )}
                  </div>

                  {/* Approval badge */}
                  {hasPendingApprovals && (
                    <button
                      onClick={() => {
                        setMealApprovalToken(approvalTokens[0]);
                        setShowMealApprovalModal(true);
                      }}
                      className="shrink-0 flex items-center gap-1 rounded-full bg-orange-100 dark:bg-orange-900/40 px-2 py-0.5 text-compact-xs font-semibold text-orange-700 dark:text-orange-300 border border-orange-200/70 dark:border-orange-700/40 active:scale-95 transition-all"
                    >
                      <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                      {approvalTokens.length}
                    </button>
                  )}

                  {/* Glucose band — informational chip (not a control) */}
                  <div className="shrink-0" role="presentation">
                    {member.glucose_band ? (
                      <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-compact-xs font-semibold ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.08] ${glucose.bg} ${glucose.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${glucose.dot}`} />
                        {glucose.label}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-compact-xs font-medium text-muted-foreground">
                        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/25" />
                        —
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
            </div>
          </div>

          {/* Pending Invites Section */}
          {pendingInvites.length > 0 && (
            <div className="rounded-2xl border border-amber-200/70 dark:border-amber-700/40 bg-gradient-to-br from-amber-50 to-yellow-50/60 dark:from-amber-950/30 dark:to-yellow-950/20 overflow-hidden shadow-sm">
              {/* Section Header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200/60 dark:border-amber-700/30">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/40">
                  <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="text-caption1 font-bold text-amber-800 dark:text-amber-300">Pending Invites</p>
                  <p className="text-compact-xs text-amber-600/70 dark:text-amber-500">Waiting for acceptance</p>
                </div>
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-compact-xs font-bold text-white">
                  {pendingInvites.length}
                </span>
              </div>

              {/* Pending list */}
              <div className="divide-y divide-amber-200/40 dark:divide-amber-700/20">
                {pendingInvites.map((invite: any, idx: number) => {
                  const initial = invite.invitee_display_name?.[0]?.toUpperCase() || invite.email?.[0]?.toUpperCase() || "?";
                  const pendingColors = ["bg-amber-400", "bg-orange-400", "bg-yellow-500", "bg-amber-500", "bg-orange-500"];
                  const colorClass = pendingColors[idx % pendingColors.length];

                  return (
                    <div
                      key={invite.email || idx}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      {/* Avatar */}
                      <div className="relative shrink-0">
                        <div className={`flex h-9 w-9 items-center justify-center rounded-full text-white font-bold text-subhead ${colorClass} opacity-70`}>
                          {initial}
                        </div>
                        {/* Pending indicator dot */}
                        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-400 border border-white dark:border-amber-950">
                          <Clock className="h-2 w-2 text-white" strokeWidth={3} />
                        </span>
                      </div>

                      {/* Name + email */}
                      <div className="min-w-0 flex-1">
                        <p className="text-caption1 font-bold text-amber-900 dark:text-amber-200 truncate">
                          {invite.invitee_display_name || "Invited User"}
                        </p>
                        {invite.email ? (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Mail className="h-2.5 w-2.5 text-amber-500/70" strokeWidth={2} />
                            <p className="text-compact-xs text-amber-700/60 dark:text-amber-400/70 truncate">
                              {invite.email}
                            </p>
                          </div>
                        ) : (
                          <p className="text-compact-xs text-amber-600/50 dark:text-amber-500/50 mt-0.5">Via link invite</p>
                        )}
                      </div>

                      {/* Relationship + status badge */}
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        {invite.relationship && (
                          <span className="text-2xs font-semibold text-amber-700/60 dark:text-amber-400/60 capitalize">
                            {invite.relationship}
                          </span>
                        )}
                        <span className="rounded-md border border-dashed border-amber-400/60 bg-transparent px-2 py-0.5 text-2xs font-semibold text-amber-700 dark:text-amber-400">
                          Pending
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Meal Approval Modal */}
      <MealApprovalModal
        approvalToken={mealApprovalToken}
        isOpen={showMealApprovalModal}
        onClose={() => {
          setShowMealApprovalModal(false);
          setMealApprovalToken(null);
        }}
      />
    </div>
  );
};

export default GroupMembersListPage;
